/**
 * ResumeOrDiscardGate
 *
 * Wraps an apply page route. When a user lands on an apply page with a
 * resumable application (either from ?resume=<id> or from authenticated status
 * lookup), the gate intercepts rendering and presents a two-step dialog:
 *
 *   Step 1 — Choice
 *     "Yes, resume"         → allow/navigate to ?resume=<id>; existing
 *                             hydration logic in ApplyPage / BusinessApplyPage
 *                             takes over.
 *     "Start a new one"     → advance to step 2.
 *
 *   Step 2 — Confirm discard
 *     "Confirm, start fresh" → POST /api/applications/discard; on success
 *                              the gate clears and the form renders fresh.
 *     "Back"                → return to step 1.
 *
 * While the auth check or application fetch is in progress, the gate renders
 * a neutral loading state (prevents the form from partially mounting before
 * the dialog is shown).
 *
 * If the user is anonymous without a resume param, or has no active draft, the
 * gate passes through to children immediately.
 *
 * Auth context is consumed from AuthProvider (via useAuth). All state resets
 * when the route unmounts so re-entering /apply always starts fresh.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuth } from '@/components/auth-provider'
import {
  buildConsumerResumeUrl,
  clearAllConsumerResumeMarkers,
  clearExpressApplicationResumeMarker,
  isExpressApplicationRecord,
  isExpressJointNewApplication,
  syncConsumerResumeMarker,
} from '@/lib/application-resume-routing'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { LoadingState } from '@/components/ui/loading'

type Platform = 'consumer' | 'business'
type ApplicationScope = 'standard' | 'express-joint-new'
type GateState = 'pending' | 'checking' | 'found' | 'clear'
type DialogStep = 'choose' | 'confirm-discard'

interface ActiveApp {
  applicationId: string
  status: string
}

interface StatusApplication {
  applicationId?: unknown
  status?: unknown
  applicationType?: unknown
  summary?: { expressFlow?: unknown }
}

interface StatusResponse {
  ok?: boolean
  data?: { applications?: StatusApplication[] }
}

interface DiscardResponse {
  ok?: boolean
  error?: string
}

interface Props {
  platform: Platform
  redirectPath: string
  /** When `express-joint-new`, only surfaces express joint-new drafts on /apply/express. */
  applicationScope?: ApplicationScope
  children: ReactNode
}

function readExpressFlow(app: StatusApplication): string | null {
  const expressFlow = app.summary?.expressFlow
  return typeof expressFlow === 'string' ? expressFlow : null
}

function matchesApplicationScope(app: StatusApplication, scope: ApplicationScope): boolean {
  const applicationId = typeof app.applicationId === 'string' ? app.applicationId : ''
  const applicationType = typeof app.applicationType === 'string' ? app.applicationType : null
  if (!applicationId) {
    return false
  }
  if (scope === 'express-joint-new') {
    return isExpressJointNewApplication({
      applicationId,
      applicationType,
      expressFlow: readExpressFlow(app),
    })
  }
  return !isExpressApplicationRecord({ applicationId, applicationType })
}

export function ResumeOrDiscardGate({
  platform,
  redirectPath,
  applicationScope = 'standard',
  children,
}: Props) {
  const { status: authStatus } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [gateState, setGateState] = useState<GateState>('pending')
  const [dialogStep, setDialogStep] = useState<DialogStep>('choose')
  const [activeApp, setActiveApp] = useState<ActiveApp | null>(null)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [discardError, setDiscardError] = useState<string | null>(null)
  const [confirmedResumeParam, setConfirmedResumeParam] = useState('')

  const resumeParam = searchParams.get('resume')?.trim() || ''
  const hasResumeParam = Boolean(resumeParam)

  useEffect(() => {
    // Wait until auth resolves.
    if (authStatus === 'loading') {
      setGateState('pending')
      return
    }

    if (hasResumeParam && confirmedResumeParam === resumeParam) {
      setGateState('clear')
      return
    }

    if (hasResumeParam) {
      if (applicationScope === 'standard') {
        if (
          platform === 'consumer' &&
          isExpressApplicationRecord({ applicationId: resumeParam })
        ) {
          navigate(buildConsumerResumeUrl({ applicationId: resumeParam }), { replace: true })
          return
        }
      } else if (
        !isExpressApplicationRecord({ applicationId: resumeParam, applicationType: 'express' })
      ) {
        navigate(`/apply?resume=${encodeURIComponent(resumeParam)}`, { replace: true })
        return
      }
      setActiveApp({ applicationId: resumeParam, status: 'in_progress' })
      setDialogStep('choose')
      setDiscardError(null)
      setGateState('found')
      return
    }

    // Anonymous users always get a fresh form.
    if (authStatus === 'unauthenticated') {
      setGateState('clear')
      return
    }

    // Authenticated and no resume param — check for an existing active draft.
    // Note: no "has-run" ref guard here. AbortController handles StrictMode's
    // double-effect invocation: cleanup aborts fetch #1 (swallowed below),
    // effect #2 starts fetch #2 and wins. A ref that persisted across the
    // simulated unmount/remount would leave the gate stuck in 'checking'
    // because the second invocation would skip the fetch entirely.
    setGateState('checking')
    const controller = new AbortController()

    ;(async () => {
      try {
        const response = await fetch(
          `/api/applications/status?platform=${encodeURIComponent(platform)}`,
          { credentials: 'include', signal: controller.signal }
        )
        if (controller.signal.aborted) { return }
        if (!response.ok) { setGateState('clear'); return }
        const json = (await response.json().catch(() => null)) as StatusResponse | null
        if (controller.signal.aborted) { return }
        const applications = json?.data?.applications
        if (!Array.isArray(applications)) { setGateState('clear'); return }

        const active = applications.find((app) => {
          const status = typeof app.status === 'string' ? app.status : ''
          const id = typeof app.applicationId === 'string' ? app.applicationId : ''
          return id && (status === 'draft' || status === 'in_progress') && matchesApplicationScope(app, applicationScope)
        })

        if (!active || typeof active.applicationId !== 'string') {
          setGateState('clear')
          return
        }

        if (
          applicationScope === 'standard' &&
          platform === 'consumer' &&
          isExpressApplicationRecord({
            applicationId: active.applicationId,
            applicationType: typeof active.applicationType === 'string' ? active.applicationType : null,
          })
        ) {
          navigate(buildConsumerResumeUrl({
            applicationId: active.applicationId,
            applicationType: typeof active.applicationType === 'string' ? active.applicationType : null,
          }), { replace: true })
          return
        }

        if (controller.signal.aborted) { return }
        setActiveApp({ applicationId: active.applicationId, status: String(active.status ?? '') })
        setGateState('found')
      } catch (err) {
        // AbortError is expected when deps change / StrictMode re-runs; do not
        // clobber the state because a later invocation is about to update it.
        if (err instanceof DOMException && err.name === 'AbortError') { return }
        setGateState('clear')
      }
    })()

    return () => { controller.abort() }
  }, [applicationScope, authStatus, confirmedResumeParam, hasResumeParam, navigate, platform, resumeParam])

  const handleResume = () => {
    if (!activeApp) { return }
    const target = applicationScope === 'express-joint-new'
      ? buildConsumerResumeUrl({ applicationId: activeApp.applicationId, applicationType: 'express' })
      : platform === 'consumer'
        ? buildConsumerResumeUrl({ applicationId: activeApp.applicationId })
        : `${redirectPath}?resume=${encodeURIComponent(activeApp.applicationId)}`
    if (applicationScope === 'express-joint-new' || platform === 'consumer') {
      syncConsumerResumeMarker({
        applicationId: activeApp.applicationId,
        applicationType: applicationScope === 'express-joint-new' ? 'express' : null,
      })
    }
    if (!hasResumeParam) {
      navigate(target, { replace: true })
    } else {
      setConfirmedResumeParam(activeApp.applicationId)
    }
    // Clear the gate so it doesn't re-trigger when the URL changes.
    setGateState('clear')
  }

  const clearResumeMarkers = () => {
    if (applicationScope === 'express-joint-new') {
      clearExpressApplicationResumeMarker()
      return
    }
    if (platform === 'consumer') {
      clearAllConsumerResumeMarkers()
      return
    }
    try {
      window.sessionStorage.removeItem('bao_application_id')
      window.sessionStorage.removeItem('bao_dashboard_invite_sent')
    } catch {
      // sessionStorage may be unavailable; the server-side discard has already
      // removed the authoritative draft.
    }
  }

  const handleWantsNew = () => {
    setDialogStep('confirm-discard')
    setDiscardError(null)
  }

  const handleBack = () => {
    setDialogStep('choose')
    setDiscardError(null)
  }

  const handleConfirmDiscard = async () => {
    if (!activeApp || isDiscarding) { return }
    setIsDiscarding(true)
    setDiscardError(null)
    try {
      if (authStatus === 'authenticated') {
        const response = await fetch('/api/applications/discard', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ applicationId: activeApp.applicationId, platform }),
        })
        const json = (await response.json().catch(() => null)) as DiscardResponse | null
        if (!response.ok || !json?.ok) {
          throw new Error(json?.error || 'Unable to discard the application.')
        }
      }
      clearResumeMarkers()
      setActiveApp(null)
      setDialogStep('choose')
      setGateState('clear')
      navigate(redirectPath, { replace: true })
    } catch (err) {
      setDiscardError(
        err instanceof Error ? err.message : 'Unable to discard the application. Please try again.'
      )
    } finally {
      setIsDiscarding(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // While resolving auth or fetching application status, show a neutral loading
  // state so the form doesn't flash before the dialog can appear.
  if (gateState === 'pending' || gateState === 'checking') {
    return <LoadingState message="Loading your account…" variant="page" />
  }

  // No active application or user is already resuming — render the page normally.
  if (gateState === 'clear') {
    return <>{children}</>
  }

  // Active application found — render the form behind a blocking dialog.
  // Blocking means no pointer-down-outside and no escape-key dismissal so
  // the user must make an explicit choice.
  return (
    <>
      {hasResumeParam ? null : children}
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {dialogStep === 'choose' ? (
            <>
              <DialogHeader>
                <DialogTitle>You have an application in progress</DialogTitle>
                <DialogDescription>
                  Would you like to resume your existing application, or start a new one?
                  Starting a new application will discard your current one.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  className="w-full bg-brand hover:bg-brand-hover text-cfb-light"
                  onClick={handleResume}
                >
                  Yes, resume my application
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={handleWantsNew}
                >
                  No, start a new one
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Discard your current application?</DialogTitle>
                <DialogDescription>
                  {authStatus === 'authenticated' ? (
                    <>
                      Your application in progress will be{' '}
                      <span className="font-semibold text-gray-900">permanently deleted</span> and
                      cannot be recovered. This action cannot be undone.
                    </>
                  ) : (
                    <>
                      We will remove this saved resume link from this browser and start a fresh
                      application. To permanently delete a server-saved draft, sign in first.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              {discardError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {discardError}
                </div>
              )}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleConfirmDiscard}
                  disabled={isDiscarding}
                >
                  {isDiscarding ? 'Discarding…' : 'Confirm, start fresh'}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={handleBack}
                  disabled={isDiscarding}
                >
                  Back
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
